import React, { useState, useEffect, useRef, useCallback } from 'react';
import DOMPurify from 'dompurify';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Avatar,
  Chip,
  Button,
  TextField,
  CircularProgress,
  Divider,
  Collapse,
  Tooltip,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SendIcon from '@mui/icons-material/Send';
import ReplyIcon from '@mui/icons-material/Reply';
import EmailIcon from '@mui/icons-material/Email';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { useNavigate } from 'react-router-dom';
import { createInboxApi } from '../../services/api';
import { replyUnavailableReason, type ThreadAccess } from '../../services/inboxAccountApi';
import { useToast } from '../../contexts/ToastContext';

interface MiniConversationCardProps {
  open: boolean;
  onClose: () => void;
  threadId: number;
  inboxType?: 'shared' | 'personal';
  /** Inline (two-pane) mode: render the reader+composer in place (no Dialog). */
  inline?: boolean;
  /** When provided, shows an "Open composer" button for a richer reply. */
  onOpenComposer?: () => void;
  /** Notified after a quick reply sends, so the parent can refresh its list. */
  onReplied?: () => void;
}

interface Message {
  id: number;
  from_name: string;
  from_email: string;
  to_emails: string;
  cc_emails?: string;
  date: string;
  snippet: string;
  body_html?: string;
  body_text?: string;
  is_sent: number;
  attachments_json?: string;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
}

export default function MiniConversationCard({ open, onClose, threadId, inboxType = 'shared', inline = false, onOpenComposer, onReplied }: MiniConversationCardProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const inboxApi = createInboxApi(inboxType);
  const [loading, setLoading] = useState(true);
  const [thread, setThread] = useState<any>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [access, setAccess] = useState<ThreadAccess | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const loadThread = useCallback(async () => {
    setLoading(true);
    try {
      const res = await inboxApi.getThread(threadId);
      if (res.success) {
        setThread(res.data.thread);
        setMessages(res.data.messages || []);
        // `access` isn't on the frozen api.ts response type yet.
        const data: typeof res.data & { access?: ThreadAccess } = res.data;
        setAccess(data.access ?? null);
        if (res.data.messages?.length) {
          setExpandedId(res.data.messages[res.data.messages.length - 1].id);
        }
      }
    } catch {
      toast.error('Failed to load conversation');
    }
    setLoading(false);
  }, [threadId, inboxType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if ((open || inline) && threadId) { setReplying(false); setReplyText(''); loadThread(); }
  }, [open, inline, threadId, loadThread]);

  useEffect(() => {
    if (!loading && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [loading, messages.length]);

  const handleSendReply = async () => {
    if (!replyText.trim() || !thread) return;
    setSending(true);
    try {
      const lastMsg = messages[messages.length - 1];
      const to = lastMsg?.is_sent ? (lastMsg.to_emails ? lastMsg.to_emails.split(',').map((e: string) => e.trim()) : []) : [lastMsg?.from_email];
      const htmlBody = replyText.replace(/\n/g, '<br/>');
      const res = await inboxApi.reply(threadId, { to, htmlBody });
      if (res.success) {
        toast.success('Reply sent');
        setReplyText('');
        setReplying(false);
        await loadThread();
        onReplied?.();
      } else {
        toast.error('Failed to send reply');
      }
    } catch {
      toast.error('Failed to send reply');
    }
    setSending(false);
  };

  // ── Header (subject + open-in-inbox + optional close) ──────────────────
  const headerBar = (
    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: inline ? 2 : 0, py: inline ? 1.5 : 0, borderBottom: inline ? 1 : 0, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <EmailIcon sx={{ fontSize: 20, color: 'primary.main', flexShrink: 0 }} />
        <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>
          {thread?.subject || 'Conversation'}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title="Open in Inbox">
          <IconButton size="small" onClick={() => {
            if (!inline) onClose();
            const path = inboxType === 'personal' ? '/email' : '/helpdesk';
            navigate(`${path}?thread=${threadId}`);
          }} aria-label="Open in Inbox">
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {!inline && (
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  );

  // ── Threaded messages ──────────────────────────────────────────────────
  const messagesEl = loading ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
      <CircularProgress size={28} />
    </Box>
  ) : messages.length === 0 ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
      <Typography color="text.secondary">No messages found</Typography>
    </Box>
  ) : (
    <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1.5 }}>
      {messages.map((msg, idx) => {
        const isExpanded = expandedId === msg.id;
        const isLast = idx === messages.length - 1;
        return (
          <Paper
            key={msg.id}
            variant="outlined"
            sx={{ mb: 1.5, borderRadius: '6px', overflow: 'hidden', borderColor: isLast ? 'primary.main' : 'divider', borderWidth: isLast ? 1.5 : 1 }}
          >
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
              onClick={() => setExpandedId(isExpanded ? null : msg.id)}
            >
              <Avatar sx={{ width: 28, height: 28, fontSize: '11px', bgcolor: msg.is_sent ? 'primary.main' : 'secondary.main' }}>
                {getInitials(msg.from_name || msg.from_email || '?')}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '12.5px' }}>
                    {msg.from_name || msg.from_email}
                  </Typography>
                  {msg.is_sent ? <Chip label="Sent" size="small" sx={{ height: 16, fontSize: '10.5px', fontWeight: 600 }} /> : null}
                </Box>
                {!isExpanded && (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontSize: '11px' }}>
                    {msg.snippet}
                  </Typography>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontSize: '10.5px' }}>
                {formatDate(msg.date)}
              </Typography>
              <IconButton size="small" sx={{ ml: 0.5 }}>
                {isExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Box>
            <Collapse in={isExpanded}>
              <Divider />
              <Box sx={{ px: 1.5, py: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10.5px' }}>To: {msg.to_emails}</Typography>
                  {msg.cc_emails && <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10.5px' }}>CC: {msg.cc_emails}</Typography>}
                </Box>
                <Box
                  sx={{
                    fontSize: '12.5px', lineHeight: 1.5, maxHeight: 300, overflow: 'auto',
                    '& img': { maxWidth: '100%' }, '& a': { color: 'primary.main' },
                    '& blockquote': { borderLeft: '3px solid', borderColor: 'divider', pl: 1.5, ml: 0, color: 'text.secondary' },
                  }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.body_html || msg.body_text?.replace(/\n/g, '<br/>') || '') }}
                />
                {msg.attachments_json && (() => {
                  try {
                    const attachments = JSON.parse(msg.attachments_json);
                    if (attachments.length === 0) return null;
                    return (
                      <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {attachments.map((att: any, i: number) => (
                          <Chip key={i} label={att.filename || 'Attachment'} size="small" variant="outlined" sx={{ fontSize: '10.5px' }} />
                        ))}
                      </Box>
                    );
                  } catch { return null; }
                })()}
              </Box>
            </Collapse>
          </Paper>
        );
      })}
      <div ref={messagesEndRef} />
    </Box>
  );

  // ── Reply section (quick reply + optional "Open composer") ─────────────
  // A teammate's client email or ticket email is readable here but not
  // answerable in place — a reply would go out from the owner's address
  // (Michael, 2026-09-16). Say who can reply; the Composer still writes a NEW
  // email from your own address.
  const unavailable = replyUnavailableReason(access);
  const replyEl = (!loading && messages.length > 0) ? (
    <Box sx={{ borderTop: 1, borderColor: 'divider', px: 2, py: 1.5 }}>
      {unavailable ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography data-testid="reply-unavailable" sx={{ flex: 1, fontSize: 12.5, color: 'var(--ink-soft)', lineHeight: 1.45 }}>
            {unavailable}
          </Typography>
          {onOpenComposer && (
            <Tooltip title="Write a new email from your own address">
              <Button variant="text" startIcon={<EditNoteIcon />} onClick={onOpenComposer} sx={{ textTransform: 'none', flexShrink: 0 }}>
                Composer
              </Button>
            </Tooltip>
          )}
        </Box>
      ) : replying ? (
        <Box>
          <TextField
            fullWidth multiline minRows={3} maxRows={6} placeholder="Type your reply..."
            value={replyText} onChange={(e) => setReplyText(e.target.value)} size="small" autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleSendReply(); } }}
            sx={{ mb: 1, '& .MuiInputBase-root': { fontSize: '13.5px' } }}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
            {onOpenComposer && (
              <Tooltip title="Open the full composer (attachments, formatting, schedule)">
                <Button size="small" startIcon={<EditNoteIcon />} onClick={onOpenComposer} sx={{ textTransform: 'none', mr: 'auto' }}>
                  Full composer
                </Button>
              </Tooltip>
            )}
            <Button size="small" onClick={() => { setReplying(false); setReplyText(''); }} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Tooltip title="Ctrl+Enter to send">
              <span>
                <Button size="small" variant="contained" startIcon={sending ? <CircularProgress size={14} /> : <SendIcon sx={{ fontSize: 14 }} />}
                  disabled={sending || !replyText.trim()} onClick={handleSendReply} sx={{ textTransform: 'none' }}>
                  Send Reply
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button fullWidth variant="outlined" startIcon={<ReplyIcon />} onClick={() => setReplying(true)} sx={{ textTransform: 'none', borderRadius: '6px' }}>
            Quick reply
          </Button>
          {onOpenComposer && (
            <Tooltip title="Open the full composer for a more in-depth reply">
              <Button variant="text" startIcon={<EditNoteIcon />} onClick={onOpenComposer} sx={{ textTransform: 'none', flexShrink: 0 }}>
                Composer
              </Button>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  ) : null;

  // ── Inline (two-pane) render ───────────────────────────────────────────
  if (inline) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        {headerBar}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
          {messagesEl}
        </Box>
        {replyEl}
      </Box>
    );
  }

  // ── Modal render (default — used by TicketTable + cold-open) ───────────
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: 1, borderColor: 'divider' }}>
        {headerBar}
      </DialogTitle>
      <DialogContent sx={{ p: 0, flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {messagesEl}
        {replyEl}
      </DialogContent>
    </Dialog>
  );
}
